import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Grid,
  SelectChangeEvent,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Search, Store as StoreIcon } from "@mui/icons-material";
import { AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  selectCategory,
  selectFloor,
  selectSortBy,
  selectSearchQuery,
  setCategory,
  setFloor,
  setSortBy,
  setSearchQuery,
  resetFilters,
  selectHasFiltersApplied,
} from "../../store/slices/storesSlice";
import { StoreCategory } from "../../types/stores";
import { useGetStoresByMallQuery } from "../../features/stores/storesApiSlice";
import { useGetMallByIdQuery } from "../../features/malls/mallsApiSlice";
import { useDebounce } from "../../hooks/useDebounce";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import {
  StoreCard,
  StoresHero,
  StoresFilterBar,
  StoresFilterBarSkeleton,
  StoresSkeleton,
} from "../../components/Stores";
import ScrollIndicator, {
  BottomFadeOverlay,
} from "../../components/common/ScrollIndicator";
import EmptyState from "../../components/Offers/EmptyState";
import { PAGINATION } from "../../config/constants";

const Stores: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { mallId: selectedMallId } = useParams<{ mallId: string }>();
  const category = useAppSelector(selectCategory);
  const floor = useAppSelector(selectFloor);
  const sortBy = useAppSelector(selectSortBy);
  const searchQuery = useAppSelector(selectSearchQuery);
  const hasFilters = useAppSelector(selectHasFiltersApplied);

  const [showFilterBar, setShowFilterBar] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedStores, setAllLoadedStores] = useState<any[]>([]);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 1000);

  // Get mall from navigation state (passed from MallHub) or fetch as fallback
  const mallFromState = location.state?.mall;
  const { data: mallFromApi } = useGetMallByIdQuery(selectedMallId || "", {
    skip: !selectedMallId || !!mallFromState, // Skip API call if we have mall from state
  });
  const selectedMall = mallFromState || mallFromApi;

  // Fetch stores with filters and pagination
  const {
    data: storesData,
    isLoading: storesLoading,
    error: storesError,
    isFetching: storesFetching,
  } = useGetStoresByMallQuery(
    {
      mallId: selectedMallId || "",
      category: category !== "all" ? category : undefined,
      floor: floor !== "all" ? floor : undefined,
      sortBy,
      searchQuery: debouncedSearchQuery,
      page: currentPage,
      limit: PAGINATION.PAGE_LIMIT,
    },
    {
      skip: !selectedMallId,
    }
  );

  // Pagination info
  const pagination = storesData?.pagination;
  const hasMore = pagination ? pagination.page < pagination.totalPages : false;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setAllLoadedStores([]);
  }, [selectedMallId, category, floor, sortBy, debouncedSearchQuery]);

  // Accumulate stores when new page loads
  useEffect(() => {
    if (storesData?.stores !== undefined) {
      setAllLoadedStores((prev) => {
        // If it's page 1, replace all stores (filters changed)
        if (currentPage === 1) {
          return storesData.stores;
        }
        // Otherwise, append new stores
        const newStores = storesData.stores.filter(
          (newStore) =>
            !prev.some((existingStore) => existingStore.id === newStore.id)
        );
        return [...prev, ...newStores];
      });
    }
  }, [storesData, currentPage]);

  const stats = storesData?.stats;
  const totalStores = !selectedMallId ? 0 : storesData?.total || 0;

  // Calculate floor and category counts
  const floorCount = stats?.floors?.length || 0;
  const categoryCount = stats?.categories?.length || 0;

  // Load next page
  const loadMore = useCallback(() => {
    if (hasMore && !storesFetching) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore, storesFetching]);

  // Setup infinite scroll
  useInfiniteScroll(loadMore, hasMore, storesFetching);

  // Use accumulated stores
  const stores = allLoadedStores;

  // Loading state
  const isLoading = selectedMallId && storesLoading;

  // Show filter bar after mall selection
  useEffect(() => {
    if (selectedMallId) {
      setTimeout(() => setShowFilterBar(true), 300);
    } else {
      setShowFilterBar(false);
    }
  }, [selectedMallId]);

  // Smooth scroll effect - content slides up over hero
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const currentScrollY = window.scrollY;
        const maxOffset = 350;
        const offset = Math.min(currentScrollY, maxOffset);
        contentRef.current.style.transform = `translateY(-${offset}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCategoryChange = (cat: StoreCategory | "all") => {
    dispatch(setCategory(cat));
  };

  const handleFloorChange = (event: SelectChangeEvent) => {
    dispatch(setFloor(event.target.value));
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    dispatch(
      setSortBy(event.target.value as "name" | "floor" | "offers" | "newest")
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(e.target.value));
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleClearSearch = () => {
    dispatch(setSearchQuery(""));
  };

  // Get available floors
  const availableFloors = stats?.floors || [];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
        pb: 0,
      }}
    >
      {/* Hero Section */}
      <StoresHero
        selectedMall={selectedMall}
        totalStores={totalStores}
        floorCount={floorCount}
        categoryCount={categoryCount}
      />

      {/* Main Content */}
      <Box
        ref={contentRef}
        sx={{
          position: "relative",
          zIndex: 1100,
          background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
          borderTopLeftRadius: { xs: 16, md: 24 },
          borderTopRightRadius: { xs: 16, md: 24 },
          boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
          willChange: "transform",
          mt: -3,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            pt: { xs: 2, md: 3 },
            pb: { xs: 3, md: 4 },
          }}
        >
          {/* Search & Filters Bar */}
          {selectedMallId && isLoading ? (
            <StoresFilterBarSkeleton />
          ) : (
            <StoresFilterBar
              showFilterBar={showFilterBar}
              category={category}
              floor={floor}
              sortBy={sortBy}
              searchQuery={searchQuery}
              hasFilters={hasFilters}
              availableFloors={availableFloors}
              onCategoryChange={handleCategoryChange}
              onFloorChange={handleFloorChange}
              onSortChange={handleSortChange}
              onSearchChange={handleSearchChange}
              onResetFilters={handleResetFilters}
              onClearSearch={handleClearSearch}
            />
          )}

          {/* Stores Grid */}
          {!selectedMallId ? (
            <EmptyState
              icon={
                <StoreIcon sx={{ fontSize: 80, color: "text.secondary" }} />
              }
              title="No Mall Selected"
              description="Please select a mall from the Explore page to view stores"
              action={
                <Box
                  sx={{
                    mt: 3,
                    px: 3,
                    py: 1.5,
                    bgcolor: "primary.main",
                    color: "white",
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "primary.dark",
                      transform: "translateY(-2px)",
                    },
                  }}
                  onClick={() => navigate("/explore")}
                >
                  <Typography variant="button" sx={{ fontWeight: 600 }}>
                    Explore Malls
                  </Typography>
                </Box>
              }
            />
          ) : isLoading ? (
            <StoresSkeleton />
          ) : storesError ? (
            <EmptyState
              icon={<Search sx={{ fontSize: 80, color: "error.main" }} />}
              title="Error Loading Stores"
              description="Failed to load stores. Please try again."
            />
          ) : stores.length === 0 ? (
            <EmptyState
              icon={<Search sx={{ fontSize: 80, color: "text.secondary" }} />}
              title="No Stores Found"
              description={
                hasFilters
                  ? "Try adjusting your filters to see more results"
                  : "No stores available at this mall yet"
              }
              action={
                hasFilters ? (
                  <Chip
                    label="Reset Filters"
                    onClick={handleResetFilters}
                    sx={{
                      background:
                        "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                      color: "white",
                      fontWeight: 600,
                      px: 3,
                      py: 3,
                      fontSize: "0.9rem",
                    }}
                  />
                ) : undefined
              }
            />
          ) : (
            <Box>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <AnimatePresence mode="wait">
                  {stores.map((store, index) => (
                    <Grid item xs={12} sm={6} md={4} key={store.id}>
                      <StoreCard store={store} index={index} />
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>

              {/* Loading indicator for infinite scroll */}
              {storesFetching && currentPage > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 4,
                    mt: 2,
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography
                    variant="body2"
                    sx={{ ml: 2, color: "text.secondary" }}
                  >
                    Loading more stores...
                  </Typography>
                </Box>
              )}

              {/* End of results message */}
              {!hasMore && stores.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 4,
                    mt: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontStyle: "italic" }}
                  >
                    You've reached the end of the stores
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Container>
      </Box>

      {/* Scroll Indicator for Mobile Users */}
      <ScrollIndicator color="primary.main" bottomOffset={100} />
      <BottomFadeOverlay color="#f5f7fa" height={100} />
    </Box>
  );
};

export default Stores;
